/**
 * 1010 一键生成流程的状态机与进度展示(纯函数,页面 / hook 只调这里)。
 *
 * 阶段:idle(填表)→ confirming(报价确认,AI 配图开且 N>0 才有)→ running(读资料 / 写文案 / 配图 x/N / 发布预览,轮询)
 *   → succeeded(去预览调整;任务 `partial` = 有图没生成成功,也算成功并提示去换图里重试)| failed(失败原因 + 重试)。
 * ★ 余额不足在报价时就拦:`canStart(quote)` 为 false 时「生成官网」与确认按钮都不让点,不开始任务。
 * ★ 重试 = 回到 idle 重新走一遍(重新报价),不在失败任务上原地续跑 —— 失败那次的积分 api 已按成功张数结算 / 退回。
 */
import type { FlowJob, FlowJobStep, ImageQuote } from "../api/flowTypes";

export type FlowPhase =
  | { kind: "idle" }
  | { kind: "confirming"; quote: ImageQuote }
  | { kind: "running"; task: FlowJob | null }
  | { kind: "succeeded"; task: FlowJob }
  | { kind: "failed"; task: FlowJob | null; error: string };

export type FlowEvent =
  | { type: "ASK_CONFIRM"; quote: ImageQuote }
  | { type: "CANCEL" }
  | { type: "START" }
  | { type: "PROGRESS"; task: FlowJob }
  | { type: "FAIL"; error: string }
  | { type: "RETRY" };

export const IDLE: FlowPhase = { kind: "idle" };

export const GENERATE_FAILED_TEXT = "生成没有完成";

export function isTerminal(task: Pick<FlowJob, "status">): boolean {
  return task.status !== "running";
}

export function flowReducer(phase: FlowPhase, ev: FlowEvent): FlowPhase {
  switch (ev.type) {
    case "ASK_CONFIRM":
      return phase.kind === "idle" ? { kind: "confirming", quote: ev.quote } : phase;
    case "CANCEL":
      return phase.kind === "confirming" ? IDLE : phase;
    case "START":
      return phase.kind === "idle" || phase.kind === "confirming"
        ? { kind: "running", task: null }
        : phase;
    case "PROGRESS":
      if (ev.task.status === "succeeded" || ev.task.status === "partial") {
        return { kind: "succeeded", task: ev.task };
      }
      if (ev.task.status === "failed") {
        return { kind: "failed", task: ev.task, error: ev.task.error || GENERATE_FAILED_TEXT };
      }
      return { kind: "running", task: ev.task };
    case "FAIL":
      return {
        kind: "failed",
        task: phase.kind === "running" ? phase.task : null,
        error: ev.error,
      };
    case "RETRY":
      return phase.kind === "failed" ? IDLE : phase;
  }
}

/** 报价允许开始吗:AI 配图关(N=0)恒可;开了要余额够。 */
export function canStart(quote: ImageQuote | null): boolean {
  if (!quote) return true;
  if (quote.aiCount === 0) return true;
  return quote.affordable;
}

/** 生成前要不要弹报价确认:AI 配图开且估出了要生成的张数。 */
export function needsConfirm(aiImages: boolean, quote: ImageQuote | null): boolean {
  return aiImages && !!quote && quote.aiCount > 0;
}

/** 「将生成约 N 张图,约 M 积分」。 */
export function quoteText(quote: ImageQuote): string {
  if (quote.aiCount === 0) return "不需要 AI 生成图片,不扣积分";
  return `将生成约 ${quote.aiCount} 张图,约 ${quote.totalCredits} 积分`;
}

/** 余额不足那句(余额拿不到时只说不足)。 */
export function shortfallText(quote: ImageQuote): string {
  return `积分不足:需要约 ${quote.totalCredits} 积分,当前 ${quote.availableCredits} 积分`;
}

// ── 进度 ──

export const GEN_STAGES: ReadonlyArray<{ key: Exclude<FlowJobStep, "done">; label: string }> = [
  { key: "sources", label: "读资料" },
  { key: "copy", label: "写文案" },
  { key: "images", label: "配图" },
  { key: "publish", label: "发布预览" },
];

export type StageState = "done" | "active" | "pending" | "failed" | "skipped";

export interface StageRow {
  key: string;
  label: string;
  state: StageState;
}

const STEP_STATE: Record<string, StageState> = {
  pending: "pending",
  running: "active",
  done: "done",
  failed: "failed",
  skipped: "skipped",
};

function stageIndex(step: FlowJobStep): number {
  if (step === "done") return GEN_STAGES.length;
  return GEN_STAGES.findIndex((s) => s.key === step);
}

/**
 * 进度分段行。api 给了 `steps` 就照它画(「配图 3/12」= label + detail);没给按 `step` 推:
 * 之前 done、当前 active(失败时 failed)、之后 pending;任务成功全 done。还没拿到任务时第一段 active。
 */
export function stageRows(task: FlowJob | null): StageRow[] {
  if (task && task.steps?.length) {
    return task.steps.map((s) => ({
      key: s.key,
      label: s.detail ? `${s.label} ${s.detail}` : s.label,
      state: STEP_STATE[s.status] ?? "pending",
    }));
  }
  const idx = task ? stageIndex(task.step) : 0;
  const succeeded = task?.status === "succeeded" || task?.status === "partial";
  const failed = task?.status === "failed";
  return GEN_STAGES.map((s, i) => {
    let state: StageState;
    if (succeeded || i < idx) state = "done";
    else if (i === idx) state = failed ? "failed" : "active";
    else state = "pending";
    const label =
      s.key === "images" && task && task.imagesTotal > 0
        ? `${s.label} ${task.imagesDone}/${task.imagesTotal}`
        : s.label;
    return { key: s.key, label, state };
  });
}

/** 成功后的一句话:配了几张、几张失败要去换图里重试、扣了多少、几条待补。 */
export function doneSummary(task: FlowJob): string {
  const parts: string[] = [];
  if (task.imagesTotal > 0) parts.push(`AI 配图 ${task.imagesDone} 张`);
  if (task.imagesFailed > 0) {
    parts.push(`${task.imagesFailed} 张没生成成功,可在「换图」里重试`);
  }
  if (task.creditsCharged > 0) parts.push(`扣 ${task.creditsCharged} 积分`);
  if (task.suggestionCount > 0) parts.push(`${task.suggestionCount} 项资料待补`);
  return parts.join(" · ");
}

// ── 补充介绍 / 自有图上限 ──

export const INTRO_MAX = 2000;
export const OWN_IMAGES_MAX = 10;
